import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchItem } from "@/lib/hn";
import DOMPurify from "dompurify";
import { formatDistanceToNow } from "date-fns";
import { MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface CommentItemProps {
  id: number;
  depth?: number;
  isRoot?: boolean;
}

const MAX_COMMENT_DEPTH = 8;

export function CommentItem({ id, depth = 0, isRoot = false }: CommentItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const { data: comment, isLoading, error } = useQuery({
    queryKey: ["item", id],
    queryFn: () => fetchItem(id),
    staleTime: 60 * 1000 * 5, // 5 min
    enabled: depth <= MAX_COMMENT_DEPTH,
  });

  // Prevent infinite recursion on deeply nested threads
  if (depth > MAX_COMMENT_DEPTH) {
    return (
      <div className="ml-6 md:ml-12 mt-2 text-xs text-on-surface-variant/60 italic">
        <a href={`https://news.ycombinator.com/item?id=${id}`} target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors">
          Continue this thread on HN →
        </a>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2 py-3 px-4 rounded-lg bg-white/5">
        <div className="h-4 bg-white/10 rounded w-1/4"></div>
        <div className="h-3 bg-white/10 rounded w-full"></div>
        <div className="h-3 bg-white/10 rounded w-5/6"></div>
      </div>
    );
  }

  if (error || !comment || comment.deleted || comment.dead || !comment.text) {
    return null; // hide deleted or failed comments
  }

  const hasKids = comment.kids && comment.kids.length > 0;
  const safeHtml = DOMPurify.sanitize(comment.text || "");

  return (
    <div className={cn("group/comment relative transition-all duration-300", isRoot ? "" : "ml-6 md:ml-12 mt-4 space-y-4 border-l-2 border-transparent")}>
      {!isRoot && (
        <div className="absolute left-[-2px] top-0 bottom-0 w-0.5 bg-white/30 rounded-full hidden sm:block"></div>
      )}
      <div className={cn("bg-white rounded-xl shadow-md transition-all duration-300", isRoot ? "p-6" : "p-5 border border-white/10")}>
        <div className="flex gap-4">
          <div className="flex flex-col items-center min-w-[1.5rem] pt-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 text-primary/40 hover:text-primary transition-colors z-10 bg-transparent"
            >
              {isExpanded ? (
                <span className="material-symbols-outlined text-[1.2rem]" style={{ fontVariationSettings: "'FILL' 1" }}>keyboard_arrow_up</span>
              ) : (
                <span className="material-symbols-outlined text-[1.2rem]">keyboard_arrow_down</span>
              )}
            </button>
            {isExpanded && hasKids && (
              <div className="absolute top-10 bottom-[-1.5rem] w-0.5 bg-gradient-to-b from-primary/30 to-transparent rounded-full group-hover/comment:from-primary/60 transition-colors hidden sm:block" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <header className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3 text-[10px] md:text-[11px] font-label uppercase tracking-widest text-on-surface-variant flex-wrap">
              <span className="text-[#1a1a1a] font-bold lowercase text-sm tracking-normal">
                {comment.by}
              </span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-400">
                {formatDistanceToNow(comment.time * 1000, { addSuffix: true })}
              </span>
              {/* Mock special badge for demonstration */}
              {comment.score && comment.score > 20 && (
                <span className="bg-secondary text-on-secondary px-2 py-0.5 rounded text-[9px] md:text-[10px] font-bold ml-1">Top Reply</span>
              )}
            </header>

            {isExpanded ? (
              <>
                <div
                  className="text-sm md:text-[0.9375rem] text-[#1a1a1a] font-body leading-relaxed [&>p]:mb-4 last:[&>p]:mb-0 [&>pre]:bg-gray-50 [&>pre]:p-4 [&>pre]:rounded [&>pre]:border [&>pre]:border-gray-100 [&>pre]:overflow-x-auto [&>pre]:text-sm [&>pre]:font-mono [&_a]:text-primary [&_a]:font-bold hover:[&_a]:underline word-break-words break-words"
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />

                <footer className="mt-3 md:mt-4 flex items-center gap-4 md:gap-6">
                  <button className="flex items-center gap-1.5 text-xs text-primary font-bold hover:text-secondary hover:bg-primary px-2 py-1 -ml-2 rounded transition-colors">
                    <span className="material-symbols-outlined text-sm">reply</span> Reply
                  </button>
                  <button className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-primary transition-colors">
                    Share
                  </button>
                </footer>

                {/* Kids Container */}
                {hasKids && (
                  <div className="mt-2 space-y-2 relative">
                    {comment.kids!.map((kidId) => (
                      <CommentItem key={kidId} id={kidId} depth={depth + 1} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={() => setIsExpanded(true)}
                className="text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-primary transition-colors mt-1"
              >
                {hasKids && (
                  <span className="flex items-center gap-1.5">
                    <MessageCircle className="w-3.5 h-3.5" />
                    {comment.kids!.length} Hidden Replies
                  </span>
                )}
                {!hasKids && "Expand Comment"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
